import "../styles/offerPolish.css";
import { RARITY_IDS, type OfferItem } from "@highmarch/domain";
import { AssetLibrary } from "../../render/assets";
import { burstHtml, pickSlotHtml, priceButtonHtml, slotTileHtml, timerChipHtml, wellHtml } from "../components";
import { uiUrl } from "../portraits";
import { ItemTile, amountTier, valueText } from "./dealsModel";

/**
 * The Deals and VIP building blocks as markup, on the design-system v2 components (ui/components.ts; "Keep & Plate"):
 * item wells, the one value burst, the gold price button with its VIP XP bubble (blue while a choice is still to make,
 * a dashed stamp when sold out or refused), the countdown chip, the empty pick slot and the gem-pack slot the Shop sheet
 * can share. Presentation only; every string comes from dealsModel.ts.
 */

/** Under this many seconds a countdown chip turns its clock red (the design system's urgent timer). */
const URGENT_SECONDS = 3600;

/** Art paths from the model (`hq:` → art/hq, `ui:` → art/interface) as URLs (single-file builds inline them). */
export function artUrl(path: string): string {
  if (path.startsWith("hq:")) return AssetLibrary.resolveInlined(`./hq/${path.slice(3)}`);
  return uiUrl(path.startsWith("ui:") ? path.slice(3) : path);
}

const esc = (s: string): string => s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", "\"": "&quot;" })[c]!);

/** A tile's rarity index for the well's jewel tint (chests and gear), or null for the neutral navy well. */
export function tileRarity(t: ItemTile): number | null {
  const i = (RARITY_IDS as readonly string[]).indexOf(t.tone);
  return i >= 0 ? i : (t.tier ?? null);
}

export type WellSize = "sm" | "md" | "lg";

/**
 * One item as a well: the jewel tint by rarity (navy for gems, gold, speed-ups, skins), the count, a
 * speed-up's length as the cap, "?" when it holds a random roll or a slot still to pick. A `button` tells what it is
 * (data-name / data-detail); `span` inside another button (a chooser row, a track stop, a prize).
 */
/**
 * The items drawn in wells, by key: a tap finds its item here (items hold Fix64 amounts, which JSON cannot carry).
 * Rebuilt as pages draw; capped so a long session never grows it without end.
 */
const ITEMS = new Map<string, OfferItem>();
let itemSeq = 0;
const ITEMS_CAP = 4000;
function itemKey(item: OfferItem): string {
  if (ITEMS.size >= ITEMS_CAP) ITEMS.clear();
  const k = `i${++itemSeq}`;
  ITEMS.set(k, item);
  return k;
}
/** The item a well with `data-item` shows (null once it was dropped). */
export function wellItem(key: string | undefined): OfferItem | null { return key ? ITEMS.get(key) ?? null : null; }

export function wellOf(t: ItemTile, size: WellSize = "sm", tag: "button" | "span" = "button", extraClass = "", attrs = ""): string {
  return wellHtml({
    art: artUrl(t.icon), rarity: tileRarity(t), size, count: t.count, cap: t.top, info: t.random || t.needsSlot, tag,
    className: `dwell${t.needsSlot ? " need" : ""}${extraClass ? ` ${extraClass}` : ""}`, label: tag === "button" ? t.name : undefined,
    data: `data-name="${esc(t.name)}" data-detail="${esc(t.detail)}"${t.item ? ` data-item="${itemKey(t.item)}"` : ""}${attrs ? ` ${attrs}` : ""}`,
  });
}

/** A pack's items on one row, each well with its short name under it (owner 2026-10-19: name every item). */
export function wellsOf(tiles: readonly ItemTile[], size: WellSize = "sm", tag: "button" | "span" = "button"): string {
  return tiles.map((t) => `<span class="dit">${wellOf(t, size, tag)}<i class="dnm">${esc(shortName(t.name))}</i></span>`).join("");
}

/** "2,000 gems" → "Gems", "8h speed-up ×2" → "Speed-up": the well already shows the count and the length. */
export function shortName(name: string): string {
  const n = name.replace(/\s*×\s*[\d,]+$/, "").replace(/^[\d,.]+[KkM]?\s+/, "").replace(/^\d+[mhd]\s+/, "").trim();
  return n ? n[0].toUpperCase() + n.slice(1) : name;
}

/** An empty Custom Chest slot: a dashed well with a gold "+" until the player fills it. */
export function plusSlotHtml(slot: number, label = "Pick"): string {
  return pickSlotHtml({ label: `${label} ${slot + 1}`, className: "dplus", data: `data-slot="${slot}"` });
}

/** The honest value (docs/SHOP.md §1.2) as the one value burst. */
export function valueBurstHtml(percent: number): string {
  return burstHtml(valueText(percent));
}

/**
 * MP-09: a recurring deal's value, stated plainly in the burst's corner instead of a percent: the gem icon and
 * "Worth 1,441" (the items' computed gem value, docs/SHOP.md §1.2). Quiet on purpose: the burst stays for one-offs.
 */
export function worthTagHtml(gems: number): string {
  const n = gems.toLocaleString("en-US");
  return `<span class="dworth" aria-label="Worth ${n} gems"><img decoding="async" src="${uiUrl("icons/gem@2x.png")}" alt=""><span>Worth <b>${n}</b></span></span>`;
}

export interface PriceButton {
  readonly offerId: string;
  readonly label: string;
  readonly vipPoints: number;
  /** "" when buyable; else the button's words ("Fill the chest", "Sold out", "A tier was bought today"). */
  readonly blocked: string;
  /** A struck-through "was" price above the price (Buy All only). */
  readonly was?: string | null;
  readonly soldOut?: boolean;
  /** Blocked on the player's own choice: the blue "choose" button that opens the chooser. */
  readonly choice?: boolean;
  /** The VIP XP bubble on the price's left shoulder (an offer card's value burst owns the top-right corner). */
  readonly bubbleLeft?: boolean;
}

/** Button heights: 52 (a page's one big purchase), 46 (a card's), 42 (a slot's). */
export type PriceSize = "main" | "card" | "slot";

/**
 * The price: always the gold primary (priceButtonHtml, the store's price, the VIP XP bubble). Blocked on a choice it
 * is the blue button naming the choice; sold out or refused it is a dashed stamp that still takes the tap (the toast
 * says why). Every variant keeps `.dbuy[data-offer]`, the hook the screen and the smoke test click.
 */
export function priceBtnHtml(p: PriceButton, size: PriceSize = "card"): string {
  const data = `data-offer="${esc(p.offerId)}"`;
  const sz = `p-${size}`;   // "p-" prefix: the kit's `.card` is the card tray's class
  if (p.soldOut) return `<button type="button" class="k-stamp dbuy sold ${sz}" ${data}>Sold out</button>`;
  if (p.blocked && p.choice) return `<button type="button" class="btn blue dbuy choose ${sz}" ${data}><span class="lbl">${esc(p.blocked)}</span></button>`;
  if (p.blocked) return `<button type="button" class="k-stamp dbuy blocked ${sz}" ${data}>${esc(p.blocked)}</button>`;
  return priceButtonHtml({ currency: "real", amount: esc(p.label), was: p.was ?? undefined, wasBelow: !!p.was, vipXp: p.vipPoints, bubble: p.bubbleLeft ? "left" : "right", className: `dbuy ${sz}`, data });
}

/** A real countdown (`data-ends` + `.ct`, which tickTimers rewrites): the navy chip, red clock in the last hour. */
export function countdownHtml(endsAt: number, text: string, now: number, label = ""): string {
  return timerChipHtml(text, { endsAt, label, urgent: endsAt - now < URGENT_SECONDS });
}

/** One gem pack as a shop slot: the pile in a navy well, the gems, the store's price in gold, value and VIP XP. */
export interface GemPackSlot {
  readonly id: string;
  readonly name: string;
  readonly gems: number;
  /** The store's own price string (or the reference tier in the sandbox). */
  readonly price: string;
  /** The pile's art (a URL or painted-icon markup: econArt.gemPackIcon). */
  readonly art: string;
  readonly value: { readonly percent: number; readonly show: boolean };
  readonly vipPoints: number;
  /** Owner 2026-10-21: the pack's first purchase pays double (the well shows what arrives, a ×2 ribbon says why). */
  readonly first?: boolean;
  readonly tag?: "popular" | "best";
}

/** The gem heap by pack size (the first pack is the smallest pile): the same art on Deals → Gems and the Shop sheet. */
const PACK_ART = [1, 2, 2, 3, 3, 4, 4];
export function gemPackArt(index: number): string {
  return uiUrl(`icons/gem_pack_${PACK_ART[Math.max(0, Math.min(index, PACK_ART.length - 1))]}@3x.png`);
}

/**
 * One gem pack slot, the same on Deals → Gems and the Shop sheet (m7 S1): `.gbuy[data-pack]` buys it. `className` adds
 * the screen's hooks (the Shop sheet's `gpack`).
 */
export function gemPackSlotHtml(g: GemPackSlot, className = ""): string {
  return slotTileHtml({
    well: { art: g.art, count: (g.first ? g.gems * 2 : g.gems).toLocaleString("en-US"), rarity: amountTier("gems", g.gems) },
    name: esc(g.name),
    burst: g.value.show ? valueBurstHtml(g.value.percent) : "",
    price: priceButtonHtml({ currency: "real", amount: esc(g.price), vipXp: g.vipPoints, className: "dbuy gbuy p-slot", data: `data-pack="${esc(g.id)}" aria-label="${esc(g.name)}, ${g.gems} gems"` }),
    className: `dgem${g.first ? " first" : ""}${g.tag ? ` tag-${g.tag}` : ""}${className ? ` ${className}` : ""}`, data: `data-pack="${esc(g.id)}"`,
  }).replace(/^(<[^>]*>)/, `$1${g.first ? `<span class="gfirst disp">×2</span>` : ""}`)
    // the Most popular / Best value ribbon under the name (clear of the value star and the VIP XP bubble)
    .replace(/(<[a-z]+ class="k-name"[^>]*>[\s\S]*?<\/[a-z]+>)/, `$1${g.tag ? `<span class="gtag">${g.tag === "best" ? "Best value" : "Most popular"}</span>` : ""}`);
}
